import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DollarSign,
  Receipt,
  RotateCcw,
  TrendingUp,
  CreditCard,
  Calendar,
  Loader2,
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';

interface ProcessingAnalyticsSectionProps {
  companyId: string;
  merchantName?: string;
}

interface TransactionStats {
  totalInvoices: number;
  totalInvoiceAmount: number;
  totalDonations: number;
  totalDonationAmount: number;
  totalRefunds: number;
  totalRefundAmount: number;
  transactionCount: number;
}

export function ProcessingAnalyticsSection({ companyId, merchantName }: ProcessingAnalyticsSectionProps) {
  // Date range state - default to current month
  const [startDate, setStartDate] = useState(() => {
    return format(startOfMonth(new Date()), 'yyyy-MM-dd');
  });
  const [endDate, setEndDate] = useState(() => {
    return format(endOfMonth(new Date()), 'yyyy-MM-dd');
  });

  // Quick date range presets
  const setDatePreset = (preset: 'today' | 'week' | 'month' | '3months' | 'year') => {
    const now = new Date();
    let start: Date;
    let end: Date = now;

    switch (preset) {
      case 'today':
        start = new Date(now.setHours(0, 0, 0, 0));
        end = new Date();
        break;
      case 'week':
        start = new Date(now);
        start.setDate(start.getDate() - 7);
        break;
      case 'month':
        start = startOfMonth(now);
        end = endOfMonth(now);
        break;
      case '3months':
        start = startOfMonth(subMonths(now, 2));
        end = endOfMonth(now);
        break;
      case 'year':
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date();
        break;
      default:
        start = startOfMonth(now);
    }

    setStartDate(format(start, 'yyyy-MM-dd'));
    setEndDate(format(end, 'yyyy-MM-dd'));
  };

  // Fetch transaction data
  const { data: stats, isLoading } = useQuery<TransactionStats>({
    queryKey: ['processing-analytics', companyId, startDate, endDate],
    queryFn: async () => {
      const normalizedCompanyId = companyId.startsWith('company_') ? companyId : `company_${companyId}`;

      // Convert date strings to timestamps for querying
      const startTimestamp = Timestamp.fromDate(new Date(startDate + 'T00:00:00'));
      const endTimestamp = Timestamp.fromDate(new Date(endDate + 'T23:59:59'));

      // Query transactions for this company within date range
      const transactionsRef = collection(db, 'transactions');
      const transactionsQuery = query(
        transactionsRef,
        where('companyId', '==', normalizedCompanyId)
      );

      const snapshot = await getDocs(transactionsQuery);

      let totalInvoices = 0;
      let totalInvoiceAmount = 0;
      let totalDonations = 0;
      let totalDonationAmount = 0;
      let totalRefunds = 0;
      let totalRefundAmount = 0;
      let transactionCount = 0;

      snapshot.docs.forEach(doc => {
        const data = doc.data();

        // Check if transaction is within date range
        let transactionDate: Date | null = null;
        if (data.createdAt?.toDate) {
          transactionDate = data.createdAt.toDate();
        } else if (data.date) {
          transactionDate = new Date(data.date);
        }

        if (!transactionDate) return;

        const startRange = new Date(startDate);
        const endRange = new Date(endDate);
        endRange.setHours(23, 59, 59, 999);

        if (transactionDate < startRange || transactionDate > endRange) {
          return;
        }

        transactionCount++;
        const amount = parseFloat(data.amount) || 0;

        // Check if it's a refund
        if (data.status === 'refunded' || data.type === 'refund' || data.transactionType === 'refund') {
          totalRefunds++;
          totalRefundAmount += Math.abs(amount);
        }
        // Check if it's a donation
        else if (data.metadata?.isDonation || data.description?.toLowerCase().includes('donation')) {
          totalDonations++;
          totalDonationAmount += amount;
        }
        // Regular invoice payment
        else if (data.status === 'paid' || data.status === 'completed') {
          totalInvoices++;
          totalInvoiceAmount += amount;
        }
      });

      return {
        totalInvoices,
        totalInvoiceAmount,
        totalDonations,
        totalDonationAmount,
        totalRefunds,
        totalRefundAmount,
        transactionCount,
      };
    },
    enabled: !!companyId,
  });

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Processing Analytics</h3>
          <p className="text-sm text-muted-foreground">
            Transaction activity for {merchantName || 'this merchant'}
          </p>
        </div>
      </div>

      {/* Date Range Selector */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[150px]">
              <Label htmlFor="startDate" className="text-sm text-muted-foreground">From</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="flex-1 min-w-[150px]">
              <Label htmlFor="endDate" className="text-sm text-muted-foreground">To</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => setDatePreset('today')}>
                Today
              </Button>
              <Button variant="outline" size="sm" onClick={() => setDatePreset('week')}>
                Last 7 Days
              </Button>
              <Button variant="outline" size="sm" onClick={() => setDatePreset('month')}>
                This Month
              </Button>
              <Button variant="outline" size="sm" onClick={() => setDatePreset('3months')}>
                Last 3 Months
              </Button>
              <Button variant="outline" size="sm" onClick={() => setDatePreset('year')}>
                YTD
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Invoice Processing */}
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-green-700">Invoice Payments</CardTitle>
            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
              <Receipt className="h-5 w-5 text-green-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-900">
              {formatCurrency(stats?.totalInvoiceAmount || 0)}
            </div>
            <p className="text-xs text-green-600 mt-1">
              {stats?.totalInvoices || 0} paid invoices
            </p>
          </CardContent>
        </Card>

        {/* Webform Payments (Donations) */}
        <Card className="bg-gradient-to-br from-blue-50 to-sky-50 border-blue-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-blue-700">Webform Payments</CardTitle>
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-blue-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-900">
              {formatCurrency(stats?.totalDonationAmount || 0)}
            </div>
            <p className="text-xs text-blue-600 mt-1">
              {stats?.totalDonations || 0} payments
            </p>
          </CardContent>
        </Card>

        {/* Refunds */}
        <Card className="bg-gradient-to-br from-red-50 to-rose-50 border-red-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-red-700">Refunds</CardTitle>
            <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
              <RotateCcw className="h-5 w-5 text-red-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-900">
              {formatCurrency(stats?.totalRefundAmount || 0)}
            </div>
            <p className="text-xs text-red-600 mt-1">
              {stats?.totalRefunds || 0} refunds issued
            </p>
          </CardContent>
        </Card>

        {/* Total Volume */}
        <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-purple-700">Total Volume</CardTitle>
            <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-purple-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-900">
              {formatCurrency((stats?.totalInvoiceAmount || 0) + (stats?.totalDonationAmount || 0))}
            </div>
            <p className="text-xs text-purple-600 mt-1">
              {stats?.transactionCount || 0} total transactions
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Period Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Date Range:</span>
              <p className="font-medium">
                {format(new Date(startDate), 'MMM d, yyyy')} - {format(new Date(endDate), 'MMM d, yyyy')}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Net Volume:</span>
              <p className="font-medium text-green-600">
                {formatCurrency(
                  (stats?.totalInvoiceAmount || 0) +
                  (stats?.totalDonationAmount || 0) -
                  (stats?.totalRefundAmount || 0)
                )}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Avg Transaction:</span>
              <p className="font-medium">
                {stats?.transactionCount && stats.transactionCount > 0
                  ? formatCurrency(
                      ((stats.totalInvoiceAmount || 0) + (stats.totalDonationAmount || 0)) /
                      (stats.totalInvoices + stats.totalDonations || 1)
                    )
                  : formatCurrency(0)}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Refund Rate:</span>
              <p className="font-medium">
                {stats?.transactionCount && stats.transactionCount > 0
                  ? `${((stats.totalRefunds / stats.transactionCount) * 100).toFixed(1)}%`
                  : '0%'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
